"use client";

import { Button, Loader, Modal } from "@devhop/ui";
import { PackageIcon, RefreshCw } from "lucide-react";
import type {
  PosSerialItem,
  ShopSellableCatalogRow,
} from "@/modules/merchant/presentation/api/client";
import { usePosSerialsForSkuQuery } from "@/modules/merchant/presentation/api/queries";
import { resolveImageSrc } from "@/shared/ui/AppImage";

type ShopPosSerialPickModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  branchId: string;
  row: ShopSellableCatalogRow | null;
  onPick: (row: ShopSellableCatalogRow, serial: PosSerialItem) => void;
};

export function ShopPosSerialPickModal({
  open,
  onOpenChange,
  shopId,
  branchId,
  row,
  onPick,
}: ShopPosSerialPickModalProps) {
  const q = usePosSerialsForSkuQuery(shopId, branchId, row?.id, open && !!row);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ເລືອກ serial"
      description={
        row ? `${row.productName} · SKU ${row.shopSku}` : "ເລືອກສິນຄ້າກ່ອນ"
      }
      size="md"
    >
      {!row ? (
        <p className="text-muted-foreground text-sm">ບໍ່ມີລາຍການ</p>
      ) : (
        <div className="space-y-4" key={row.id}>
          <div className="flex gap-3 rounded-lg border border-border bg-muted/30 p-3">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted">
              {(() => {
                const src = row.imageUrl ? resolveImageSrc(row.imageUrl) : null;
                return src ? (
                  <img
                    src={src}
                    alt=""
                    className="size-full rounded-lg object-cover"
                  />
                ) : (
                  <PackageIcon className="size-6 text-secondary" />
                );
              })()}
            </div>
            <div className="min-w-0">
              <p className="font-bold font-heading text-card-foreground text-sm">
                {row.productName}
              </p>
              <p className="font-mono text-muted-foreground text-xs">
                {row.shopSku}
              </p>
            </div>
          </div>

          {q.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : q.isError ? (
            <p className="text-destructive text-sm">
              {q.error instanceof Error ? q.error.message : "ໂຫຼດລົ້ມເຫຼວ"}
            </p>
          ) : !q.data?.items.length ? (
            <div className="space-y-3">
              <p className="text-muted-foreground text-sm">
                ບໍ່ມີ serial ສະຖານະພ້ອມຂາຍ (in_stock) ໃນສາຂານີ້ — ຮັບເຂົ້າ serial ຕາມ lot
                ກ່ອນ
              </p>
              {(row.quantityOnHand ?? 0) > 0.009 ? (
                <p className="rounded-md border border-amber-500/35 bg-amber-500/5 px-3 py-2 text-amber-950 text-xs dark:text-amber-100">
                  ສະຕ໋ອງລວມສະແດງ{" "}
                  <span className="font-semibold tabular-nums">
                    {row.quantityOnHand}
                  </span>{" "}
                  ຊິ້ນ ແຕ່ບໍ່ມີແຖວ serial — ມັກເກີດຈາກປັບຍອດລວມມື (lot/serial ບໍ່ໃຊ້ປັບມື)
                  ຫຼືຂໍ້ມູນບໍ່ຕົງກັນ; ກວດຮັບເຂົ້າ ຫຼື ປະຫວັດສະຕ໋ອກ
                </p>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={q.isFetching}
                onClick={() => void q.refetch()}
              >
                {q.isFetching ? <Loader /> : <RefreshCw className="size-4" />}
                ໂຫຼດຄືນ
              </Button>
            </div>
          ) : (
            <ul className="max-h-[min(50vh,20rem)] space-y-2 overflow-y-auto pr-1">
              {q.data.items.map((serial) => (
                <li key={serial.id}>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-auto w-full justify-start border-border py-3 font-mono text-sm hover:border-primary/40"
                    onClick={() => {
                      onPick(row, serial);
                      onOpenChange(false);
                    }}
                  >
                    <span className="flex flex-col items-start gap-0.5 text-left">
                      <span>{serial.serialNo}</span>
                      {(serial.lotNo || serial.unitScanCode) && (
                        <span className="font-normal text-muted-foreground text-xs">
                          {[
                            serial.lotNo ? `Lot ${serial.lotNo}` : null,
                            serial.unitScanCode
                              ? `Scan ${serial.unitScanCode}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}
                    </span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
